import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { CronExecution, CronExecutionStatus } from '../../types/cron.js';

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '\u2014';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return 'laeuft...';
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const diffMs = end - start;
  if (isNaN(diffMs) || diffMs < 0) return '\u2014';

  if (diffMs < 1000) return `${diffMs}ms`;
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  return `${mins}m ${remainSecs}s`;
}

function statusLabel(status: CronExecutionStatus): string {
  switch (status) {
    case 'success': return 'Erfolgreich';
    case 'failed': return 'Fehlgeschlagen';
    case 'timeout': return 'Timeout';
    case 'running': return 'Laeuft';
  }
}

@customElement('cp-cron-execution-log')
export class CpCronExecutionLog extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) executions: CronExecution[] = [];
  @property({ type: String }) jobName = '';

  @state() private _expandedId: number | null = null;

  private _toggleExpand(id: number): void {
    this._expandedId = this._expandedId === id ? null : id;
  }

  render() {
    return html`
      <div class="cp-cron-execution-log">
        <div class="cp-cron-execution-log__header">
          <h3 class="cp-cron-execution-log__title">
            Ausfuehrungen${this.jobName ? html` &ndash; ${this.jobName}` : nothing}
          </h3>
          <button class="cp-cron-execution-log__close-btn" @click=${this._onClose} title="Schliessen">&times;</button>
        </div>
        ${this.executions.length === 0
          ? this._renderEmpty()
          : this._renderList()
        }
      </div>
    `;
  }

  private _onClose(): void {
    this.dispatchEvent(new CustomEvent('log-closed', { bubbles: true, composed: true }));
  }

  private _renderEmpty() {
    return html`
      <div class="cp-cron-execution-log__empty">
        Noch keine Ausfuehrungen
      </div>
    `;
  }

  private _renderList() {
    return html`
      <div class="cp-cron-execution-log__list">
        ${this.executions.map(exec => this._renderEntry(exec))}
      </div>
    `;
  }

  private _renderEntry(exec: CronExecution) {
    const isExpanded = this._expandedId === exec.id;
    const statusCls = `cp-cron-execution-log__status--${exec.status}`;

    return html`
      <div class="cp-cron-execution-log__entry ${isExpanded ? 'cp-cron-execution-log__entry--expanded' : ''}">
        <button
          class="cp-cron-execution-log__entry-header"
          @click=${() => this._toggleExpand(exec.id)}
        >
          <span class="cp-cron-execution-log__expand-icon">${isExpanded ? '\u25BC' : '\u25B6'}</span>
          <span class="cp-cron-execution-log__status ${statusCls}">
            ${statusLabel(exec.status)}
          </span>
          <span class="cp-cron-execution-log__time">${formatDateTime(exec.started_at)}</span>
          <span class="cp-cron-execution-log__duration">${formatDuration(exec.started_at, exec.completed_at)}</span>
          <span class="cp-cron-execution-log__trigger ${exec.triggered_by === 'manual' ? 'cp-cron-execution-log__trigger--manual' : ''}">
            ${exec.triggered_by === 'manual' ? 'manuell' : 'geplant'}
          </span>
        </button>
        ${isExpanded ? this._renderDetail(exec) : nothing}
      </div>
    `;
  }

  private _renderDetail(exec: CronExecution) {
    const hasOutput = exec.output && exec.output.trim();
    const hasError = exec.error && exec.error.trim();

    return html`
      <div class="cp-cron-execution-log__detail">
        ${hasOutput ? html`
          <div class="cp-cron-execution-log__detail-section">
            <div class="cp-cron-execution-log__detail-label">Output</div>
            <pre class="cp-cron-execution-log__detail-output">${exec.output}</pre>
          </div>
        ` : nothing}
        ${hasError ? html`
          <div class="cp-cron-execution-log__detail-section">
            <div class="cp-cron-execution-log__detail-label cp-cron-execution-log__detail-label--error">Fehler</div>
            <pre class="cp-cron-execution-log__detail-output cp-cron-execution-log__detail-output--error">${exec.error}</pre>
          </div>
        ` : nothing}
        ${!hasOutput && !hasError ? html`
          <div class="cp-cron-execution-log__detail-empty">Kein Output verfuegbar</div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cron-execution-log': CpCronExecutionLog;
  }
}
